import {
  createFileRoute, redirect,
} from '@tanstack/react-router';
import AdditionalTradeReferencesWithToken from '../../../../components/pages/additional-trade-references-with-token';
import {
  isAdditionalTradeReferencesNotFound,
  isAdditionalTradeReferencesUnauthorized,
  isNotFoundError,
  isUnauthorizedError,
  NotFoundErrorCode,
} from '../../../../utils/errors';
import { getSettingsByUrlSlug } from '../../../../api/settings/get-by-url-slug';
import { getAdditionalTradeReferencesData } from '../../../../api/submissions/get-additional-trade-references';
import { AdditionalTradeReferencesLoaderResponse } from '../additional-trade-references';

export interface AdditionalTradeReferencesSearchParams {
  token?: string;
}

interface AdditionalTradeReferencesWithTokenLoaderParams {
  params: {
    accountSlug: string;
    submissionId: string;
    token: string;
  };
}

async function additionalTradeReferencesLoader ({
  params: {
    accountSlug,
    submissionId,
    token: routeToken,
  },
}: AdditionalTradeReferencesWithTokenLoaderParams): Promise<AdditionalTradeReferencesLoaderResponse> {
  try {
    const additionalTradeReferencesData = await getAdditionalTradeReferencesData(accountSlug, submissionId, routeToken);

    // Remove token from URL after successful verification to clean up browser history
    // Using `replace: true` ensures the token URL doesn't remain in browser history
    if (routeToken) {
      // Redirect to clean URL without token
      throw redirect({
        to: `/${accountSlug}/${submissionId}/additional-trade-references`,
        replace: true,
      });
    }

    return {
      settings: additionalTradeReferencesData.settings,
    };
  } catch (error) {
    if (isNotFoundError(error) && error.code === NotFoundErrorCode.SettingsNotFound) {
      throw redirect({ to: '/' });
    }

    if (isAdditionalTradeReferencesNotFound(error) ||
      isAdditionalTradeReferencesUnauthorized(error) ||
      isUnauthorizedError(error) ||
      (isNotFoundError(error) && error.code === NotFoundErrorCode.SubmissionNotFound)) {
      const settings = await getSettingsByUrlSlug(accountSlug);
      return {
        settings,
        error,
      };
    }

    throw error;
  }
}

/**
 * Returns route configuration options for additional trade references routes
 * Can be spread directly in the route configuration
 */
function additionalTradeReferencesRouteLoader () {
  return {
    // Load data using the validated params
    loader: async ({ params }: AdditionalTradeReferencesWithTokenLoaderParams) =>
      additionalTradeReferencesLoader({ params }),
  };
}

export const Route = createFileRoute('/$accountSlug/$submissionId/additional-trade-references-token/$token')({
  ...additionalTradeReferencesRouteLoader(),
  component: AdditionalTradeReferencesWithToken,
});
